import React, { useContext, useEffect, useMemo, useState } from 'react';
import { db } from '../firebase';
import { AuthContext } from '../Firebase/AuthProvider';
import { REVIEW_COINS, earnsCoins, isBusinessAccount, reviewCoinsDue, reviewLedgerId } from '../Data/alterations';
import { BUSINESSES_COLLECTION, COINS_COLLECTION } from '../Data/offers';

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: '5', label: '5 Star' },
  { key: '4', label: '4 Star' },
  { key: '3', label: '3 Star' },
  { key: 'low', label: '1-2 Star' },
];

const toDate = (timestamp) => {
  if (!timestamp) return null;
  try {
    return timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  } catch {
    return null;
  }
};

const formatCreatedAt = (timestamp) => {
  const date = toDate(timestamp);
  if (!date) return 'Just now';
  return date.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
};

const getInitials = (name, email) => {
  const src = (name || email || 'U').trim();
  const parts = src.split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase();
};

function Stars({ rating = 0, size = 16 }) {
  return (
    <span style={{ display: 'inline-flex', gap: 2 }}>
      {[1, 2, 3, 4, 5].map((n) => (
        <svg key={n} width={size} height={size} viewBox="0 0 24 24" fill={n <= rating ? '#d8b36a' : 'none'} stroke={n <= rating ? '#d8b36a' : '#cfc0a4'} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
      ))}
    </span>
  );
}

function StatCard({ label, value, tone }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e2d7c6', borderRadius: 4, padding: '11px 12px', minWidth: 0 }}>
      <div style={{ fontSize: '1.18rem', fontWeight: 900, color: tone, lineHeight: 1 }}>{value}</div>
      <div style={{ marginTop: 5, fontSize: '0.64rem', color: '#6A7079', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</div>
    </div>
  );
}

const chipStyle = {
  fontSize: '0.73rem',
  color: '#67543b',
  background: '#F7F8F9',
  border: '1px solid #D7DBDF',
  borderRadius: 3,
  padding: '4px 9px',
  maxWidth: 280,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const dangerButtonStyle = {
  background: '#fff',
  border: '1px solid #f0cfca',
  borderRadius: 4,
  padding: '8px 11px',
  cursor: 'pointer',
  color: '#b33b2f',
  fontSize: '0.74rem',
  fontWeight: 900,
  height: 35,
  display: 'inline-flex',
  alignItems: 'center',
};

function ReviewAvatar({ item }) {
  const [errored, setErrored] = useState(false);
  const showImg = item.userPhoto && !errored;
  return (
    <div style={{ width: 52, height: 52, borderRadius: '50%', overflow: 'hidden', background: '#17110a', color: '#d8b36a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: '1rem', flexShrink: 0, border: '2px solid #e6d4ad' }}>
      {showImg ? (
        <img src={item.userPhoto} alt="" onError={() => setErrored(true)} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : (
        getInitials(item.name, item.email)
      )}
    </div>
  );
}

function ReviewCard({ item, onDelete, onStatus }) {
  const approved = item.status === 'approved';
  const photos = Array.isArray(item.photos) ? item.photos.filter(Boolean) : [];
  return (
    <article style={{ background: '#fff', border: '1px solid #D7DBDF', borderRadius: 4, padding: 16, boxShadow: '0 1px 2px rgba(31,22,10,0.04)' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '52px minmax(0, 1fr) auto', gap: 14, alignItems: 'start' }}>
        <ReviewAvatar item={item} />
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
            <h3 style={{ margin: 0, color: '#17110a', fontSize: '1rem', fontWeight: 900, lineHeight: 1.25 }}>
              {item.name || 'Anonymous'}
            </h3>
            <Stars rating={Number(item.rating) || 0} />
            <span style={{ fontSize: '0.74rem', color: '#6A7079', fontWeight: 650 }}>{formatCreatedAt(item.createdAt)}</span>
            <span style={{ fontSize: '0.68rem', fontWeight: 900, letterSpacing: '0.08em', textTransform: 'uppercase', padding: '3px 8px', borderRadius: 3, background: approved ? '#3e4a2f' : '#EEF0F2', color: approved ? '#f6f0e6' : '#6A7079' }}>
              {approved ? 'On website' : 'Not shown'}
            </span>
          </div>

          <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginBottom: item.review ? 10 : 0 }}>
            {item.productName && <span style={chipStyle}><b>Product:</b> {item.productName}</span>}
            {item.orderId && <span style={chipStyle}><b>Order:</b> #{item.orderId}</span>}
            {item.email && <span style={chipStyle}><b>Email:</b> {item.email}</span>}
            {item.verified && <span style={{ ...chipStyle, background: '#e8f4ec', color: '#187044' }}>Verified buyer</span>}
            {earnsCoins(item) && (
              <span style={{ ...chipStyle, background: item.coins ? '#e8f4ec' : '#fdf3dc', color: item.coins ? '#187044' : '#8a5a00' }}>
                {item.coins ? `${Number(item.coins.amount || REVIEW_COINS).toLocaleString('en-IN')} coins given` : `${REVIEW_COINS.toLocaleString('en-IN')} coins when shown`}
              </span>
            )}
          </div>

          {item.review && (
            <p style={{ margin: 0, color: '#5e4d39', fontSize: '0.86rem', lineHeight: 1.6, background: '#F7F8F9', border: '1px solid #D7DBDF', borderRadius: 4, padding: '10px 13px', fontStyle: 'italic' }}>
              “{item.review}”
            </p>
          )}

          {/* Photos the customer added of themselves wearing it. */}
          {photos.length > 0 && (
            <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
              {photos.map((src) => (
                <a key={src} href={src} target="_blank" rel="noopener noreferrer">
                  <img src={src} alt="Customer wearing the outfit" style={{ width: 72, height: 92, objectFit: 'cover', borderRadius: 3, border: '1px solid #D7DBDF', display: 'block' }} />
                </a>
              ))}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          {approved && reviewCoinsDue(item) && (
            <button type="button" onClick={() => onStatus(item.id, 'approved')} style={{ ...dangerButtonStyle, color: '#fff', background: '#3e4a2f', borderColor: '#3e4a2f' }}>Give {REVIEW_COINS.toLocaleString('en-IN')} coins</button>
          )}
          {approved ? (
            <button type="button" onClick={() => onStatus(item.id, 'hidden')} style={{ ...dangerButtonStyle, color: '#17110a', borderColor: '#D7DBDF' }}>Hide from website</button>
          ) : (
            <button type="button" onClick={() => onStatus(item.id, 'approved')} style={{ ...dangerButtonStyle, color: '#fff', background: '#3e4a2f', borderColor: '#3e4a2f' }}>
              {reviewCoinsDue({ ...item, status: 'approved' }) ? `Show on website + give ${REVIEW_COINS.toLocaleString('en-IN')} coins` : 'Show on website'}
            </button>
          )}
          <button type="button" onClick={() => onDelete(item.id)} style={dangerButtonStyle}>Delete</button>
        </div>
      </div>
    </article>
  );
}

function ReviewsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    const unsubscribe = db.collection('Reviews')
      .orderBy('createdAt', 'desc')
      .limit(300)
      .onSnapshot(
        (snapshot) => {
          setItems(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
          setLoading(false);
        },
        (error) => {
          console.error('Error fetching reviews:', error);
          setLoading(false);
        }
      );

    return () => unsubscribe();
  }, []);

  const matchesFilter = (item) => {
    const rating = Number(item.rating) || 0;
    if (filter === 'all') return true;
    if (filter === 'low') return rating <= 2;
    return rating === Number(filter);
  };

  const filteredItems = useMemo(() => {
    const query = search.trim().toLowerCase();
    return items.filter((item) => {
      if (!matchesFilter(item)) return false;
      if (!query) return true;
      return [item.name, item.email, item.orderId, item.productName, item.review]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(query);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, filter, search]);

  const counts = useMemo(() => {
    return items.reduce((summary, item) => {
      const rating = Number(item.rating) || 0;
      summary.all += 1;
      if (rating <= 2) summary.low += 1;
      else summary[String(rating)] = (summary[String(rating)] || 0) + 1;
      return summary;
    }, { all: 0, '5': 0, '4': 0, '3': 0, low: 0 });
  }, [items]);

  const avgRating = useMemo(() => {
    if (items.length === 0) return '0.0';
    const total = items.reduce((sum, item) => sum + (Number(item.rating) || 0), 0);
    return (total / items.length).toFixed(1);
  }, [items]);

  // Only approved reviews appear on the website's home page. Approving a
  // review gives its coins once per piece: the review and the piece's ledger
  // line are checked inside the same transaction, so a second click, or a
  // second review of the same piece, adds nothing. A review approved before
  // coins were given gets a "Give coins" button.
  // Coins are for personal accounts; a business account earns none.
  const { user } = useContext(AuthContext) || {};
  const handleStatus = async (id, status) => {
    try {
      const item = items.find((r) => r.id === id) || {};
      if (status !== 'approved' || !reviewCoinsDue({ ...item, status })) {
        await db.collection('Reviews').doc(id).update({ status, statusAt: new Date() });
        return;
      }
      const reviewRef = db.collection('Reviews').doc(id);
      const walletRef = db.collection(COINS_COLLECTION).doc(item.account);
      const lineRef = walletRef.collection('ledger').doc(reviewLedgerId(item));
      const bizRef = db.collection(BUSINESSES_COLLECTION).doc(item.account);
      await db.runTransaction(async (tx) => {
        const [review, wallet, line, biz] = await Promise.all([tx.get(reviewRef), tx.get(walletRef), tx.get(lineRef), tx.get(bizRef)]);
        const now = new Date();
        const data = review.data() || {};
        if (data.coins || line.exists || isBusinessAccount(biz.exists ? biz.data() : null)) { tx.update(reviewRef, { status, statusAt: now }); return; }
        const balance = Math.max(0, Math.floor(Number((wallet.exists && wallet.data().balance) || 0)));
        tx.set(walletRef, { balance: balance + REVIEW_COINS, updatedAt: now }, { merge: true });
        tx.set(lineRef, { change: REVIEW_COINS, reason: 'review', reviewId: id, orderId: data.orderId || '', at: now });
        tx.update(reviewRef, { status, statusAt: now, coins: { amount: REVIEW_COINS, at: now, by: user?.email || 'admin' } });
      });
    } catch (error) {
      console.error('Error updating review:', error);
      window.alert(`Could not update the review. (${error?.message || error})`);
    }
  };

  const handleDelete = async (id) => {
    try {
      await db.collection('Reviews').doc(id).delete();
    } catch (error) {
      console.error('Error deleting review:', error);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6A7079', fontWeight: 800 }}>
        Loading reviews...
      </div>
    );
  }

  return (
    <div style={{ padding: '22px 26px 34px', minHeight: '100vh', background: '#EEF0F2', boxSizing: 'border-box' }}>
      <div style={{ position: 'sticky', top: 0, zIndex: 20, background: 'rgba(244,241,235,0.96)', backdropFilter: 'blur(12px)', borderBottom: '1px solid #ded4c4', paddingBottom: 14, marginBottom: 18 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: '0.72rem', color: '#6A7079', fontWeight: 900, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 5 }}>
              MAVIINCI Voices
            </div>
            <h2 style={{ margin: 0, color: '#17110a', fontSize: '1.65rem', fontWeight: 900, lineHeight: 1.05 }}>
              Customer Reviews
            </h2>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(92px, 1fr))', gap: 8, flex: '0 1 360px' }}>
            <StatCard label="Total" value={counts.all} tone="#17110a" />
            <StatCard label="Avg Rating" value={`${avgRating} ★`} tone="#6A7079" />
            <StatCard label="5 Star" value={counts['5']} tone="#187044" />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(260px, 1fr) auto', gap: 10, alignItems: 'center', marginBottom: 10 }}>
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search name, product, order, review..."
            style={{ width: '100%', padding: '11px 13px', borderRadius: 4, border: '1px solid #d8cab5', background: '#fff', fontSize: '0.86rem', color: '#2c1e0a', outline: 'none', boxSizing: 'border-box' }}
          />
          <div style={{ display: 'flex', gap: 6, background: '#fff', border: '1px solid #d8cab5', borderRadius: 4, padding: 5, overflowX: 'auto' }}>
            {FILTERS.map((item) => {
              const active = filter === item.key;
              return (
                <button key={item.key} type="button" onClick={() => setFilter(item.key)} style={{ background: active ? '#17110a' : 'transparent', color: active ? '#fff' : '#6f604e', border: 'none', borderRadius: 9, padding: '8px 11px', fontSize: '0.74rem', fontWeight: 850, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
                  {item.label}
                  <span style={{ background: active ? 'rgba(255,255,255,0.2)' : '#e8ddd0', color: active ? '#fff' : '#6A7079', borderRadius: 3, padding: '1px 7px', fontSize: '0.68rem', fontWeight: 800, minWidth: 20, textAlign: 'center' }}>{counts[item.key] || 0}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div style={{ fontSize: '0.86rem', color: '#6f604e', fontWeight: 800, marginBottom: 12 }}>
        {filteredItems.length} visible
      </div>

      {filteredItems.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '64px 20px', background: '#fff', borderRadius: 4, border: '1px solid #D7DBDF' }}>
          <h4 style={{ margin: '0 0 8px', fontWeight: 800, color: '#14110d', fontSize: '1.1rem' }}>No reviews found</h4>
          <p style={{ margin: 0, fontSize: '0.88rem', color: '#8c7558' }}>
            Customer reviews from order support will appear here in real time.
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filteredItems.map((item) => (
            <ReviewCard key={item.id} item={item} onDelete={handleDelete} onStatus={handleStatus} />
          ))}
        </div>
      )}
    </div>
  );
}

export default ReviewsPage;
